// @ts-check
// Painel "Quem é" da Caixa (contrato 10 de atendimento, seções 5 e 18): quem está falando e como foi identificado,
// "Trocar quem fala", pedir e conferir o código de confirmação, ligar o número, restrição judicial em destaque, alunos
// com o que pode ser informado, irmãos, risco de evasão (componente da retenção), atendimentos anteriores e os cards
// de captação da família. Os campos que o papel não lê chegam nulos e não aparecem.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getWhoIs, linkThreadNumber, requestStrongCode, updateServiceThread, updateTicket, verifyStrongCode } from '../../data/service-desk.js';
import { activeLegalRestrictions, needsStrongVerification, speakerChoices, summarizeDisclosure } from '../../lib/service-who-is.js';
import { formatShortDate } from '../../lib/format.js';
import { navigate } from '../../app/router.js';
import { openRecord } from '../side-panel/open-record.js';
import { HouseholdRetentionSummary } from '../retention/household-retention-summary.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Tag } from '../../ui/chip.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { TicketValueTag, serviceErrorMessage, showServiceError, ticketValueLabel } from './service-display.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @param {{ title: string, icon: string, children: any, testId?: string, action?: any }} props */
function WhoIsSection({ title, icon, children, testId, action }) {
  return html`<section class="service-who-section" data-testid=${testId}>
    <h3 class="service-who-section-title"><${Icon} name=${icon} size=${14} /><span>${title}</span>${action ?? null}</h3>
    ${children}
  </section>`;
}

/**
 * Pedir e conferir o código de confirmação (seção 5.4).
 * @param {{ threadId: string, personId: string, onVerified: () => void }} props
 */
function StrongCodeFlow({ threadId, personId, onVerified }) {
  const [step, setStep] = useState(/** @type {'idle' | 'sent'} */ ('idle'));
  const [channel, setChannel] = useState(/** @type {'EMAIL' | 'WHATSAPP_REGISTERED'} */ ('EMAIL'));
  const [request, setRequest] = useState(/** @type {{ codeId: string, destinationMask: string, devCode?: string } | null} */ (null));
  const [code, setCode] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isBusy, setIsBusy] = useState(false);
  const send = async () => {
    setIsBusy(true);
    setError(null);
    try {
      const response = await requestStrongCode(threadId, { personId, channel, purpose: 'SENSITIVE_TOPIC' });
      setRequest(response);
      setStep('sent');
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsBusy(false);
    }
  };
  const verify = async () => {
    if (!request) return;
    setIsBusy(true);
    setError(null);
    try {
      await verifyStrongCode(threadId, { codeId: request.codeId, code: code.trim() });
      showSuccessSnackbar(t('serviceDesk.whoIs.code.verified'));
      onVerified();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsBusy(false);
    }
  };
  return html`<div class="service-who-code" data-testid="service-strong-code">
    ${step === 'idle'
      ? html`<div class="service-inline-form">
          <select class="text-input" value=${channel} aria-label=${t('serviceDesk.whoIs.code.channel')}
            onChange=${(/** @type {Event} */ event) => setChannel(/** @type {any} */ (/** @type {HTMLSelectElement} */ (event.currentTarget).value))}>
            <option value="EMAIL">${t('serviceDesk.whoIs.code.channels.EMAIL')}</option>
            <option value="WHATSAPP_REGISTERED">${t('serviceDesk.whoIs.code.channels.WHATSAPP_REGISTERED')}</option>
          </select>
          <${Button} title=${t('serviceDesk.whoIs.code.send')} icon="IconSend" size="small" variant="secondary" isLoading=${isBusy} onClick=${send} />
        </div>`
      : html`<div class="service-inline-form">
          <span class="input-hint">${t('serviceDesk.whoIs.code.sentTo', { destination: request?.destinationMask ?? '' })}</span>
          ${request?.devCode ? html`<span class="input-hint">${t('serviceDesk.whoIs.code.devCode', { code: request.devCode })}</span>` : null}
          <input class="text-input" inputmode="numeric" maxLength=${6} value=${code} placeholder="000000" aria-label=${t('serviceDesk.whoIs.code.input')}
            onInput=${(/** @type {Event} */ event) => setCode(/** @type {HTMLInputElement} */ (event.currentTarget).value.replace(/\D/g, ''))} />
          <${Button} title=${t('serviceDesk.whoIs.code.verify')} size="small" accent="blue" disabled=${code.length !== 6} isLoading=${isBusy} onClick=${verify} />
        </div>`}
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
  </div>`;
}

/**
 * @param {{ ticketId: string | null, threadId: string | null, mode: 'column' | 'overlay' | 'sheet', onClose: () => void, onChanged: () => void, reloadKey: number }} props
 */
export function ServiceWhoIsPanel({ ticketId, threadId, mode, onClose, onChanged, reloadKey }) {
  const ticket = ticketId ? { id: ticketId } : null;
  const [whoIs, setWhoIs] = useState(/** @type {any} */ (null));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));
  const [isCodeOpen, setIsCodeOpen] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkNote, setLinkNote] = useState('');
  const [speakerChoice, setSpeakerChoice] = useState(/** @type {{ personId: string, firstName: string } | null} */ (null));
  const [speakerNote, setSpeakerNote] = useState('');
  const speakerAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const studentAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isSpeakerMenuOpen, setIsSpeakerMenuOpen] = useState(false);
  const [isStudentMenuOpen, setIsStudentMenuOpen] = useState(false);

  const load = () => {
    if (!threadId && !ticketId) return;
    getWhoIs({ threadId, ticketId })
      .then((data) => {
        setWhoIs(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };
  useEffect(() => {
    setStatus('loading');
    setWhoIs(null);
    setIsCodeOpen(false);
    load();
  }, [threadId, ticketId, reloadKey]);

  const changeSpeaker = async () => {
    if (!threadId || !speakerChoice) return;
    try {
      await updateServiceThread(threadId, { identifiedPersonId: speakerChoice.personId, ...(speakerNote.trim() ? { identityNote: speakerNote.trim() } : {}) });
      showSuccessSnackbar(t('serviceDesk.whoIs.speakerChanged', { name: speakerChoice.firstName }));
      setSpeakerChoice(null);
      setSpeakerNote('');
      load();
      onChanged();
    } catch (error) {
      showServiceError(error);
    }
  };

  /** @param {string} personId */
  const chooseStudent = async (personId) => {
    if (!ticket) return;
    try {
      await updateTicket(ticket.id, { studentPersonIds: [personId] });
      showSuccessSnackbar(t('serviceDesk.whoIs.studentChanged'));
      load();
      onChanged();
    } catch (error) {
      showServiceError(error);
    }
  };

  const linkNumber = async () => {
    if (!threadId || !whoIs?.speaker) return;
    try {
      await linkThreadNumber(threadId, { personId: whoIs.speaker.personId, note: linkNote.trim() });
      showSuccessSnackbar(t('serviceDesk.whoIs.numberLinked'));
      setIsLinkOpen(false);
      setLinkNote('');
      load();
    } catch (error) {
      showServiceError(error);
    }
  };

  const header = html`<header class="service-who-header">
    <${Icon} name="IconId" size=${16} /><span class="service-who-title">${t('serviceDesk.whoIs.title')}</span>
    <${LightIconButton} icon="IconX" ariaLabel=${t('serviceDesk.whoIs.close')} onClick=${onClose} />
  </header>`;
  if (status === 'loading') {
    return html`<aside class="service-who" data-mode=${mode} data-testid="service-who-is">${header}<div class="service-who-body"><${Skeleton} height=${48} /><${Skeleton} height=${80} /><${Skeleton} height=${60} /></div></aside>`;
  }
  if (status === 'error' || !whoIs) {
    return html`<aside class="service-who" data-mode=${mode} data-testid="service-who-is">${header}<div class="service-who-body">
      <${EmptyState} icon="IconId" title=${t('serviceDesk.whoIs.errorTitle')} action=${html`<${Button} title=${t('serviceDesk.list.retry')} size="small" variant="secondary" onClick=${load} />`} />
    </div></aside>`;
  }
  const restrictions = activeLegalRestrictions(whoIs.links);
  const choices = speakerChoices(whoIs);
  const otherStudents = [...(whoIs.siblings ?? [])];
  return html`<aside class="service-who" data-mode=${mode} data-testid="service-who-is" aria-label=${t('serviceDesk.whoIs.title')}>
    ${header}
    <div class="service-who-body">
      ${restrictions.length > 0
        ? html`<div class="service-legal-alert" role="alert" data-testid="service-legal-restriction">
            <${Icon} name="IconShieldLock" size=${16} />
            <div><strong>${t('serviceDesk.whoIs.legalRestrictionTitle')}</strong>
              ${restrictions.map((restriction) => html`<span>${t('serviceDesk.whoIs.legalRestriction', { family: restriction.householdName, kind: t(`serviceDesk.values.legalRestriction.${restriction.kind}`) })}</span>`)}
            </div>
          </div>`
        : null}
      <${WhoIsSection} title=${t('serviceDesk.whoIs.speaker')} icon="IconUser" testId="service-who-speaker">
        ${whoIs.speaker
          ? html`<div class="service-who-person">
              <${Avatar} placeholder=${whoIs.speaker.name} seed=${whoIs.speaker.personId} size="lg" type="rounded" avatarUrl=${whoIs.speaker.avatarUrl ?? null} />
              <div class="service-who-person-texts">
                <button type="button" class="service-link" onClick=${() => openRecord({ objectNameSingular: 'person', recordId: whoIs.speaker.personId })}>${whoIs.speaker.name}</button>
                <span class="service-who-person-meta">
                  <${TicketValueTag} field="identityLevel" value=${whoIs.speaker.identityLevel} icon="IconShieldCheck" />
                  ${whoIs.speaker.identifiedAt ? html`<span>${t('serviceDesk.whoIs.identifiedAt', { date: formatShortDate(whoIs.speaker.identifiedAt) })}</span>` : null}
                </span>
              </div>
            </div>`
          : html`<p class="input-hint">${t('serviceDesk.whoIs.unknownSpeaker')}</p>`}
        ${threadId
          ? html`<div class="service-who-actions">
              ${choices.length > 0
                ? html`<button type="button" ref=${speakerAnchorRef} class="service-chip-button" onClick=${() => setIsSpeakerMenuOpen(!isSpeakerMenuOpen)}>
                    <${Icon} name="IconSwitchHorizontal" size=${14} />${t('serviceDesk.whoIs.changeSpeaker')}
                  </button>
                  <${Dropdown} anchorRef=${speakerAnchorRef} isOpen=${isSpeakerMenuOpen} onClose=${() => setIsSpeakerMenuOpen(false)} width=${220} role="menu">
                    <${DropdownMenuItems}>
                      ${choices.map((choice) => html`<${MenuItem} key=${choice.personId} text=${choice.firstName} icon="IconUser" onClick=${() => {
                        setSpeakerChoice(choice);
                        setIsSpeakerMenuOpen(false);
                      }} />`)}
                    <//>
                  <//>`
                : null}
              ${whoIs.speaker && needsStrongVerification(whoIs.speaker.identityLevel)
                ? html`<button type="button" class="service-chip-button" onClick=${() => setIsCodeOpen(!isCodeOpen)}><${Icon} name="IconShieldCheck" size=${14} />${t('serviceDesk.whoIs.askCode')}</button>`
                : null}
              ${whoIs.speaker ? html`<button type="button" class="service-chip-button" onClick=${() => setIsLinkOpen(!isLinkOpen)}><${Icon} name="IconPhone" size=${14} />${t('serviceDesk.whoIs.linkNumber')}</button>` : null}
            </div>`
          : null}
        ${speakerChoice
          ? html`<div class="service-inline-panel">
              <span>${t('serviceDesk.whoIs.confirmSpeaker', { name: speakerChoice.firstName })}</span>
              <textarea class="text-input service-textarea" rows="2" value=${speakerNote} placeholder=${t('serviceDesk.whoIs.speakerNotePlaceholder')}
                onInput=${(/** @type {Event} */ event) => setSpeakerNote(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
              <span class="input-hint">${t('serviceDesk.whoIs.speakerNoteHint')}</span>
              <div class="service-inline-actions">
                <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${() => setSpeakerChoice(null)} />
                <${Button} title=${t('serviceDesk.whoIs.confirm')} size="small" accent="blue" disabled=${speakerNote.trim().length > 0 && speakerNote.trim().length < 10} onClick=${changeSpeaker} />
              </div>
            </div>`
          : null}
        ${isCodeOpen && threadId && whoIs.speaker ? html`<${StrongCodeFlow} threadId=${threadId} personId=${whoIs.speaker.personId} onVerified=${() => {
          setIsCodeOpen(false);
          load();
          onChanged();
        }} />` : null}
        ${isLinkOpen
          ? html`<div class="service-inline-panel">
              <span>${t('serviceDesk.whoIs.linkNumberHint')}</span>
              <textarea class="text-input service-textarea" rows="2" value=${linkNote} placeholder=${t('serviceDesk.whoIs.linkNotePlaceholder')}
                onInput=${(/** @type {Event} */ event) => setLinkNote(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
              <div class="service-inline-actions">
                <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${() => setIsLinkOpen(false)} />
                <${Button} title=${t('serviceDesk.whoIs.linkNumber')} size="small" accent="blue" disabled=${linkNote.trim().length < 10} onClick=${linkNumber} />
              </div>
            </div>`
          : null}
      <//>
      ${(whoIs.links ?? []).length > 0
        ? html`<${WhoIsSection} title=${t('serviceDesk.whoIs.family')} icon="IconHome">
            <ul class="service-who-list">
              ${whoIs.links.map((/** @type {any} */ link) => html`<li key=${link.householdId}>
                <button type="button" class="service-link" onClick=${() => openRecord({ objectNameSingular: 'household', recordId: link.householdId })}>${link.householdName}</button>
                <span class="service-who-detail">${[
                  link.kinship && hasTranslation(`serviceDesk.whoIs.kinship.${link.kinship}`) ? t(`serviceDesk.whoIs.kinship.${link.kinship}`) : null,
                  link.isFinancialGuardian ? t('serviceDesk.whoIs.financialGuardian') : null,
                  link.isLegalGuardian ? t('serviceDesk.whoIs.legalGuardian') : null,
                ].filter(Boolean).join(' · ')}</span>
              </li>`)}
            </ul>
          <//>`
        : null}
      <${WhoIsSection} title=${t('serviceDesk.whoIs.students')} icon="IconSchool" testId="service-who-students"
        action=${ticket && otherStudents.length > 0 ? html`<span class="service-who-section-action">
          <button type="button" ref=${studentAnchorRef} class="service-chip-button" onClick=${() => setIsStudentMenuOpen(!isStudentMenuOpen)}>${t('serviceDesk.whoIs.changeStudent')}</button>
          <${Dropdown} anchorRef=${studentAnchorRef} isOpen=${isStudentMenuOpen} onClose=${() => setIsStudentMenuOpen(false)} width=${220} role="menu" placement="bottom-end">
            <${DropdownMenuItems}>
              ${otherStudents.map((/** @type {any} */ student) => html`<${MenuItem} key=${student.personId} text=${student.name} icon="IconSchool" onClick=${() => {
                setIsStudentMenuOpen(false);
                chooseStudent(student.personId);
              }} />`)}
            <//>
          <//>
        </span>` : null}>
        ${(whoIs.students ?? []).length === 0
          ? html`<p class="input-hint">${t('serviceDesk.whoIs.noStudents')}</p>`
          : html`<ul class="service-who-list">
              ${whoIs.students.map((/** @type {any} */ student) => {
                const summary = summarizeDisclosure(student.disclosure);
                return html`<li key=${student.personId} class="service-who-student">
                  <button type="button" class="service-link" onClick=${() => openRecord({ objectNameSingular: 'person', recordId: student.personId })}>${student.name}</button>
                  <span class="service-who-detail">${[student.gradeLevel, student.classOffering?.name, student.campus?.name].filter(Boolean).join(' · ')}</span>
                  <span class="service-who-tags">
                    ${student.enrollmentStatus ? html`<${Tag} color="green" text=${t(`serviceDesk.whoIs.enrollmentStatus.${student.enrollmentStatus}`)} />` : null}
                    ${student.financialStatus ? html`<${Tag} color=${student.financialStatus === 'OVERDUE' ? 'red' : 'gray'} text=${t(`serviceDesk.whoIs.financialStatus.${student.financialStatus}`)} icon="IconCoins" />` : null}
                    ${student.reEnrollmentStatus === 'AT_RISK' ? html`<${Tag} color="orange" text=${t('serviceDesk.whoIs.reEnrollmentAtRisk')} />` : null}
                  </span>
                  <span class="service-disclosure" data-tone=${summary.tone}><${Icon} name=${summary.tone === 'blocked' ? 'IconLock' : 'IconInfoCircle'} size=${12} />${t(`serviceDesk.whoIs.disclosure.${summary.key}`)}</span>
                </li>`;
              })}
            </ul>`}
      <//>
      ${otherStudents.length > 0
        ? html`<${WhoIsSection} title=${t('serviceDesk.whoIs.siblings')} icon="IconUsers">
            <ul class="service-who-list">${otherStudents.map((/** @type {any} */ sibling) => html`<li key=${sibling.personId}>
              <span>${sibling.name}</span>${sibling.enrollmentStatus ? html`<span class="service-who-detail">${t(`serviceDesk.whoIs.enrollmentStatus.${sibling.enrollmentStatus}`)}</span>` : null}
            </li>`)}</ul>
          <//>`
        : null}
      ${whoIs.retention
        ? html`<${WhoIsSection} title=${t('serviceDesk.whoIs.retention')} icon="IconHeartHandshake" testId="service-who-retention">
            <div class="service-who-retention">
              ${whoIs.retention.risk ? html`<${Tag} color=${{ LOW: 'green', MEDIUM: 'orange', HIGH: 'red' }[/** @type {'LOW'} */ (whoIs.retention.risk)] ?? 'gray'} text=${t(`serviceDesk.whoIs.retentionRisk.${whoIs.retention.risk}`)} />` : null}
              <span class="service-who-detail">${t('serviceDesk.whoIs.retentionScore', { score: whoIs.retention.score })}</span>
              ${whoIs.retention.openCaseTicketId
                ? html`<button type="button" class="service-link" onClick=${() => navigate(`/service/inbox/${whoIs.retention.openCaseTicketId}`)}>${t('serviceDesk.whoIs.openCase')}</button>`
                : null}
            </div>
            <${HouseholdRetentionSummary} householdId=${whoIs.links?.[0]?.householdId ?? null} retention=${whoIs.retention} isEmbedded=${true} />
          <//>`
        : null}
      <${WhoIsSection} title=${t('serviceDesk.whoIs.previousTickets')} icon="IconHistory" testId="service-who-previous">
        ${(whoIs.previousTickets ?? []).length === 0
          ? html`<p class="input-hint">${t('serviceDesk.whoIs.noPreviousTickets')}</p>`
          : html`<ul class="service-who-list">
              ${whoIs.previousTickets.map((/** @type {any} */ previous) => html`<li key=${previous.id}>
                <button type="button" class="service-link" onClick=${() => navigate(`/service/inbox/${previous.id}`)}>
                  <span class="service-protocol">${previous.protocol}</span>${previous.subject}
                </button>
                <span class="service-who-tags">
                  <${TicketValueTag} field="status" value=${previous.status} />
                  ${previous.category ? html`<span class="service-who-detail">${ticketValueLabel('category', previous.category)}</span>` : null}
                  ${previous.csatRating ? html`<${TicketValueTag} field="csatRating" value=${previous.csatRating} icon="IconStar" />` : null}
                </span>
              </li>`)}
            </ul>`}
      <//>
      ${(whoIs.captureCards ?? []).length > 0
        ? html`<${WhoIsSection} title=${t('serviceDesk.whoIs.captureCards')} icon="IconTargetArrow">
            <ul class="service-who-list">${whoIs.captureCards.map((/** @type {any} */ card) => html`<li key=${card.id}>
              <button type="button" class="service-link" onClick=${() => openRecord({ objectNameSingular: 'enrollmentOpportunity', recordId: card.id })}>${card.name}</button>
            </li>`)}</ul>
          <//>`
        : null}
      ${whoIs.lastContactAt ? html`<p class="service-who-footer">${t('serviceDesk.whoIs.lastContact', { date: formatShortDate(whoIs.lastContactAt) })}</p>` : null}
    </div>
  </aside>`;
}
